/* eslint-disable react-refresh/only-export-components */
import { observable, runInAction } from 'mobx'
import { createContext, useCallback, useContext, useMemo, useRef } from 'react'
import { useDeepCompareEffect } from 'use-deep-compare'
import { safeSetLocalStorage } from '~/Helpers/SafeStorage.js'

/**
 * Identifies the entity that "owns" a collapse-state key, so the startup eviction
 * sweep can delete keys whose owning control/connection no longer exists.
 * Keys without an owner (fixed UI sections) are never evicted by the known-ID sweep.
 * See CollapseStorage.ts.
 */
export type CollapseEvictionOwner = {
	kind: 'control' | 'connection'
	id: string
}

interface CollapsedState {
	// @deprecated
	defaultCollapsed?: boolean
	defaultExpandedAt: Record<string, boolean | undefined> | undefined
	ids: Record<string, boolean | undefined>
	// When this key was last written/loaded. Used by size-capped eviction.
	lastUsedAt: number | undefined
	// Which entity owns this key, for known-id eviction. Absent for fixed keys.
	owner: CollapseEvictionOwner | undefined
}

export type PanelCollapseDefaultCollapsed = boolean | ((panelId: string) => boolean)

export interface PanelCollapseHelper {
	setAllCollapsed: (parentId: string | null, panelIds: string[]) => void
	setAllExpanded: (parentId: string | null, panelIds: string[]) => void
	canExpandAll(parentId: string | null, panelIds: string[]): boolean
	canCollapseAll(parentId: string | null, panelIds: string[]): boolean
	setPanelCollapsed: (panelId: string, collapsed: boolean) => void
	setMultipleCollapsed: (panelIds: readonly string[], collapsed: boolean) => void
	togglePanelCollapsed: (parentId: string | null, panelId: string) => void
	isPanelCollapsed: (parentId: string | null, panelId: string) => boolean
}

class PanelCollapseHelperStore implements PanelCollapseHelper {
	readonly #storageId: string | null
	readonly #defaultCollapsed: PanelCollapseDefaultCollapsed
	readonly #owner: CollapseEvictionOwner | undefined

	readonly #defaultExpandedAt = observable.map<string | null, boolean>()
	readonly #ids = observable.map<string, boolean>()

	constructor(
		storageId: string | null,
		defaultCollapsed: PanelCollapseDefaultCollapsed = false,
		owner: CollapseEvictionOwner | undefined
	) {
		this.#storageId = storageId ? `companion_ui_collapsed_${storageId}` : null
		this.#defaultCollapsed = defaultCollapsed
		this.#owner = owner

		// Try loading the old state (skip if in-memory mode)
		if (this.#storageId) {
			runInAction(() => {
				try {
					const oldState = window.localStorage.getItem(this.#storageId!)
					if (oldState) {
						const parsedState: CollapsedState = JSON.parse(oldState)
						if (typeof parsedState.defaultCollapsed === 'boolean') {
							this.#defaultExpandedAt.set(null, !parsedState.defaultCollapsed)
							delete parsedState.defaultCollapsed
						} else {
							for (const [key, value] of Object.entries(parsedState.defaultExpandedAt || {})) {
								if (typeof value === 'boolean') this.#defaultExpandedAt.set(key, value)
							}
						}

						// Fixup a serialization issue
						const stringifiedNull = this.#defaultExpandedAt.get('null')
						if (stringifiedNull !== undefined) {
							this.#defaultExpandedAt.set(null, stringifiedNull)
							this.#defaultExpandedAt.delete('null')
						}

						for (const [key, value] of Object.entries(parsedState.ids)) {
							if (typeof value === 'boolean') this.#ids.set(key, value)
						}

						// Refresh lastUsedAt (and backfill owner metadata) so this key counts as recently used
						this.#writeState()
					}
				} catch (_e) {
					// Ignore
				}
			})
		}
	}

	#writeState() {
		if (!this.#storageId) return // In-memory mode, no persistence
		safeSetLocalStorage(
			this.#storageId,
			JSON.stringify({
				defaultExpandedAt: Object.fromEntries(this.#defaultExpandedAt.toJSON()),
				ids: Object.fromEntries(this.#ids.toJSON()),
				lastUsedAt: Date.now(),
				owner: this.#owner,
			} satisfies CollapsedState)
		)
	}

	setAllCollapsed = (parentId: string | null, panelIds: string[]) => {
		runInAction(() => {
			this.#defaultExpandedAt.set(parentId, true)

			// Clear state for specified panels
			for (const panelId of panelIds) {
				this.#ids.delete(panelId)
			}

			this.#writeState()
		})
	}

	setAllExpanded = (parentId: string | null, panelIds: string[]): void => {
		runInAction(() => {
			this.#defaultExpandedAt.set(parentId, false)

			// Clear state for specified panels
			for (const panelId of panelIds) {
				this.#ids.delete(panelId)
			}

			this.#writeState()
		})
	}

	canExpandAll = (parentId: string | null, panelIds: string[]): boolean => {
		return this.#defaultExpandedAt.get(parentId) || !panelIds.every((id) => !this.isPanelCollapsed(parentId, id))
	}
	canCollapseAll = (parentId: string | null, panelIds: string[]): boolean => {
		return !this.#defaultExpandedAt.get(parentId) || !panelIds.every((id) => this.isPanelCollapsed(parentId, id))
	}

	setPanelCollapsed = (panelId: string, collapsed: boolean): void => {
		runInAction(() => {
			this.#ids.set(panelId, collapsed)

			this.#writeState()
		})
	}

	setMultipleCollapsed = (panelIds: readonly string[], collapsed: boolean): void => {
		runInAction(() => {
			for (const panelId of panelIds) {
				this.#ids.set(panelId, collapsed)
			}

			this.#writeState()
		})
	}

	togglePanelCollapsed = (parentId: string | null, panelId: string): void => {
		runInAction(() => {
			const currentState = this.isPanelCollapsed(parentId, panelId)
			this.#ids.set(panelId, !currentState)

			this.#writeState()
		})
	}

	isPanelCollapsed = (parentId: string | null, panelId: string): boolean => {
		const storedValue = this.#ids.get(panelId)
		if (storedValue !== undefined) return storedValue

		const parentDefault = this.#defaultExpandedAt.get(parentId)
		if (parentDefault !== undefined) return parentDefault

		if (typeof this.#defaultCollapsed === 'function') return this.#defaultCollapsed(panelId)
		return this.#defaultCollapsed
	}

	clearUnknownIds = (knownPanelIds: readonly string[]): void => {
		runInAction(() => {
			const knownPanelIdsSet = new Set(knownPanelIds)

			for (const key of this.#ids.keys()) {
				if (!knownPanelIdsSet.has(key)) {
					this.#ids.delete(key)
				}
			}

			this.#writeState()
		})
	}
}

const PanelCollapseHelperContext = createContext<PanelCollapseHelper | null>(null)
export function usePanelCollapseHelperContext(): PanelCollapseHelper {
	const context = useContext(PanelCollapseHelperContext)
	if (!context) throw new Error('PanelCollapseHelperContext not found')
	return context
}

export interface PanelCollapseHelperForPanel {
	// doCollapse: () => void
	// doExpand: () => void
	setCollapsed: (collapsed: boolean) => void
	toggleCollapsed: () => void
	isCollapsed: boolean
}

export function usePanelCollapseHelperContextForPanel(
	ownerId: string | null,
	panelId: string
): PanelCollapseHelperForPanel {
	const panelCollapseHelper = usePanelCollapseHelperContext()

	return {
		// doCollapse: useCallback(() => panelCollapseHelper.setPanelCollapsed(panelId, true), [panelCollapseHelper, panelId]),
		// doExpand: useCallback(() => panelCollapseHelper.setPanelCollapsed(panelId, false), [panelCollapseHelper, panelId]),
		setCollapsed: useCallback(
			(collapsed: boolean) => panelCollapseHelper.setPanelCollapsed(panelId, collapsed),
			[panelCollapseHelper, panelId]
		),
		toggleCollapsed: useCallback(
			() => panelCollapseHelper.togglePanelCollapsed(ownerId, panelId),
			[panelCollapseHelper, ownerId, panelId]
		),
		isCollapsed: panelCollapseHelper.isPanelCollapsed(ownerId, panelId),
	}
}

export function PanelCollapseHelperProvider({
	storageId,
	knownPanelIds,
	defaultCollapsed,
	evictionOwner,
	children,
}: React.PropsWithChildren<{
	storageId: string
	knownPanelIds: readonly string[] | null
	defaultCollapsed?: boolean
	evictionOwner?: CollapseEvictionOwner
}>): React.JSX.Element {
	const helper = usePanelCollapseHelper(storageId, knownPanelIds, defaultCollapsed, evictionOwner)

	return <PanelCollapseHelperContext.Provider value={helper}>{children}</PanelCollapseHelperContext.Provider>
}

export function usePanelCollapseHelper(
	storageId: string | null,
	knownPanelIds: readonly string[] | null,
	defaultCollapsed: PanelCollapseDefaultCollapsed = false,
	evictionOwner?: CollapseEvictionOwner
): PanelCollapseHelper {
	// Depend on the owner's primitive fields rather than the (inline) object reference
	const ownerKind = evictionOwner?.kind
	const ownerId = evictionOwner?.id
	const store = useMemo(
		() =>
			new PanelCollapseHelperStore(
				storageId,
				defaultCollapsed,
				ownerKind && ownerId ? { kind: ownerKind, id: ownerId } : undefined
			),
		[storageId, defaultCollapsed, ownerKind, ownerId]
	)

	// Clear out unknown panel IDs (null = never prune, for callers that only know a subset of the keys)
	useDeepCompareEffect(() => {
		if (knownPanelIds) store.clearUnknownIds(knownPanelIds)
	}, [store, knownPanelIds])

	return store
}

export interface PanelCollapseAccordionProps {
	value: string[]
	onValueChange: (openIds: readonly string[]) => void
	/** Spread onto each `Accordion.Trigger`; alt+clicking a header expands/collapses every section at once. */
	getTriggerProps: (panelId: string) => { title?: string; onClickCapture?: React.MouseEventHandler }
}

/**
 * Bridge a collapse helper to a controlled multi-open accordion's `value`/`onValueChange`. The callback is
 * stable across renders (panel ids are read through a ref) so it does not churn the accordion each render.
 */
export function usePanelCollapseAccordionProps(
	helper: PanelCollapseHelper,
	panelIds: readonly string[],
	parentId: string | null = null
): PanelCollapseAccordionProps {
	const panelIdsRef = useRef(panelIds)
	panelIdsRef.current = panelIds

	const onValueChange = useCallback(
		(openIds: readonly string[]) => {
			for (const id of panelIdsRef.current) helper.setPanelCollapsed(id, !openIds.includes(id))
		},
		[helper]
	)

	const canToggleAll = panelIds.length > 1
	const getTriggerProps = useCallback(
		(panelId: string) => {
			if (!canToggleAll) return {}
			return {
				title: 'Alt+click to expand/collapse all sections',
				onClickCapture: (e: React.MouseEvent) => {
					if (!e.altKey) return
					e.stopPropagation()
					const isExpanded = !helper.isPanelCollapsed(parentId, panelId)
					helper.setMultipleCollapsed(panelIdsRef.current, isExpanded)
				},
			}
		},
		[helper, parentId, canToggleAll]
	)

	return {
		value: panelIds.filter((id) => !helper.isPanelCollapsed(parentId, id)),
		onValueChange,
		getTriggerProps,
	}
}

export interface PanelCollapseHelperLite {
	setAllCollapsed: () => void
	setAllExpanded: () => void
	canExpandAll(): boolean
	canCollapseAll(): boolean
	setPanelCollapsed: (panelId: string, collapsed: boolean) => void
	isPanelCollapsed: (panelId: string) => boolean
}

export function usePanelCollapseHelperLite(
	storageId: string,
	knownPanelIds: string[],
	defaultCollapsed = false,
	evictionOwner?: CollapseEvictionOwner
): PanelCollapseHelperLite {
	const panelIdsRef = useRef<string[]>(knownPanelIds)
	panelIdsRef.current = knownPanelIds

	const collapseHelper = usePanelCollapseHelper(storageId, knownPanelIds, defaultCollapsed, evictionOwner)

	return useMemo(
		() => ({
			setAllCollapsed: () => collapseHelper.setAllCollapsed(null, panelIdsRef.current),
			setAllExpanded: () => collapseHelper.setAllExpanded(null, panelIdsRef.current),
			canExpandAll: () => collapseHelper.canExpandAll(null, panelIdsRef.current),
			canCollapseAll: () => collapseHelper.canCollapseAll(null, panelIdsRef.current),
			setPanelCollapsed: collapseHelper.setPanelCollapsed,
			isPanelCollapsed: (panelId) => collapseHelper.isPanelCollapsed(null, panelId),
		}),
		[collapseHelper]
	)
}
